/*
 * tq-cart-bridge — glue for the free-shipping bar's region toggle
 * (snippets/tq-free-shipping.liquid).
 *
 * The chosen region (Québec/Ontario vs rest of Canada) is stored as the cart
 * attribute `tq_region` so the bar can be fully server-rendered: every section
 * re-render (add to cart, quantity change) picks it up for free, and the value
 * carries into the order's additional details. localStorage mirrors the choice
 * so it survives checkout (a completed checkout starts a fresh cart without
 * attributes).
 *
 * Loaded on every page by layout/theme.liquid. The header cart icon and badge
 * need no bridging: the icon carries id="cart-icon-bubble", which
 * assets/cart-drawer.js binds to open the drawer and the stock cart JS
 * re-renders via the cart-icon-bubble section (see snippets/tq-cart-icon.liquid).
 *
 * Also drives the cascara slot (snippets/tq-cascara-slot.liquid): one-click
 * AJAX add in the drawer and the session-long "No thanks".
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'tq_region';
  var saving = false;

  function readSaved() {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      return null;
    }
  }

  function sectionsToUpdate() {
    var sections = [];
    if (document.querySelector('cart-drawer')) sections.push('cart-drawer');
    var page = document.getElementById('main-cart-items');
    if (page && page.dataset.id) sections.push(page.dataset.id);
    return sections;
  }

  // Swap only the pieces that live inside the drawer / cart page — #CartDrawer,
  // its overlay and .drawer__inner keep their elements and listeners.
  function applySections(sections) {
    if (!sections) return;

    if (sections['cart-drawer']) {
      var drawerHtml = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      ['cart-drawer-items', '.cart-drawer__footer'].forEach(function (selector) {
        var target = document.querySelector(selector);
        var source = drawerHtml.querySelector(selector);
        if (target && source) target.replaceWith(source);
      });
    }

    var page = document.getElementById('main-cart-items');
    if (page && page.dataset.id && sections[page.dataset.id]) {
      var pageHtml = new DOMParser().parseFromString(sections[page.dataset.id], 'text/html');
      var target = page.querySelector('.js-contents');
      var source = pageHtml.querySelector('.js-contents');
      if (target && source) target.innerHTML = source.innerHTML;
    }
  }

  function setRegion(region, focusAfter) {
    if (saving) return;
    saving = true;

    try {
      localStorage.setItem(STORAGE_KEY, region);
    } catch (e) {
      /* private mode — the cart attribute still persists for the session */
    }

    var body = { attributes: { tq_region: region } };
    var sections = sectionsToUpdate();
    if (sections.length) {
      body.sections = sections;
      body.sections_url = window.location.pathname;
    }

    fetch(window.routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (state) {
        applySections(state && state.sections);
        if (focusAfter) {
          var pressed = document.querySelector('[data-tq-region="' + region + '"]');
          if (pressed) pressed.focus();
        }
      })
      .catch(function (error) {
        console.error(error);
      })
      .finally(function () {
        saving = false;
      });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-tq-region]');
    if (!button || button.getAttribute('aria-pressed') === 'true') return;
    setRegion(button.getAttribute('data-tq-region'), true);
  });

  // ---- mobile mini bar / expanded sheet ------------------------------------
  // The mini cart bar and the sheet handle are only visible on small screens
  // (tq-cart-drawer.css); the sheet-expanded class lives on <cart-drawer> so
  // it survives all the section re-render paths.
  function setSheetExpanded(drawer, expanded) {
    drawer.classList.toggle('sheet-expanded', expanded);
    // collapsing clears any dragged height so a later tap reopens at the
    // default 76dvh; a drag re-sets --tq-sheet-h itself after expanding
    if (!expanded) drawer.style.removeProperty('--tq-sheet-h');
    drawer.querySelectorAll('[data-tq-sheet-toggle]').forEach(function (toggle) {
      toggle.setAttribute('aria-expanded', String(expanded));
    });
  }

  // a real drag ends with a synthetic click; swallow it so the drag's own
  // resolved state isn't immediately toggled back by the tap handler below
  var suppressClickUntil = 0;

  // tap on the mini bar or the handle strip (the mini's checkout button
  // submits the cart form and must not toggle)
  document.addEventListener('click', function (event) {
    if (event.target.closest('[name="checkout"]')) return;
    if (Date.now() < suppressClickUntil) {
      suppressClickUntil = 0;
      return;
    }
    var toggle = event.target.closest('[data-tq-sheet-toggle]');
    if (!toggle) return;
    var drawer = toggle.closest('cart-drawer');
    if (!drawer) return;
    setSheetExpanded(drawer, !drawer.classList.contains('sheet-expanded'));
  });

  // the mini bar is a div[role=button] — support Enter/Space
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    var toggle = event.target.closest('[data-tq-sheet-toggle][role="button"]');
    if (!toggle) return;
    event.preventDefault();
    var drawer = toggle.closest('cart-drawer');
    if (drawer) setSheetExpanded(drawer, !drawer.classList.contains('sheet-expanded'));
  });

  // ── fluid drag-to-resize (mobile bottom sheet) ──────────────────────────
  // Live finger tracking: the shopper pulls the sheet to any height and it
  // stays there (free height), or flicks to open/collapse. Drag starts from
  // the mini bar, the sheet header/handle, or — like a native bottom sheet —
  // from the item list when it's already scrolled to the top and pulled down.
  // The live height rides on --tq-sheet-h on <cart-drawer> (see the CSS), an
  // element that survives every section re-render path.
  var MOBILE_MAX = 640;
  var drag = null;

  function isMobile() {
    return window.matchMedia('(max-width: ' + MOBILE_MAX + 'px)').matches;
  }

  function innerHeightPx(drawer) {
    var inner = drawer.querySelector('.drawer__inner');
    return inner ? inner.getBoundingClientRect().height : 0;
  }

  function clampHeight(h) {
    return Math.max(120, Math.min(Math.round(window.innerHeight * 0.92), h));
  }

  document.addEventListener(
    'pointerdown',
    function (event) {
      if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
      if (!isMobile()) return;
      var drawer = document.querySelector('cart-drawer.tq-drawer:not(.is-empty)');
      if (!drawer) return;

      var target = event.target;
      if (target.closest('[name="checkout"]')) return; // never hijack checkout
      var onHandle = target.closest('.sheet-mini, .tq-drawer .drawer__header, .tq-drawer .sheet-toggle');
      var items = target.closest('cart-drawer-items');

      if (!onHandle) {
        // From the list: only arm on non-interactive area, scrolled to the top,
        // so ordinary scrolling and the qty/region/remove controls still work.
        if (!items) return;
        if (target.closest('button, a, input, select, textarea')) return;
        if (items.scrollTop > 0) return;
      }

      drag = {
        drawer: drawer,
        startY: event.clientY,
        startH: innerHeightPx(drawer),
        fromList: !onHandle,
        lastY: event.clientY,
        lastT: event.timeStamp,
        velocity: 0,
        moved: false,
      };
    },
    { passive: true }
  );

  document.addEventListener(
    'pointermove',
    function (event) {
      if (!drag) return;
      var dy = event.clientY - drag.startY;

      // A list-originated drag only takes over when pulling DOWN from the top;
      // an upward move means "scroll the list", so let it go.
      if (drag.fromList && dy < 0 && !drag.moved) {
        drag = null;
        return;
      }
      if (!drag.moved && Math.abs(dy) < 4) return;
      drag.moved = true;

      drag.drawer.classList.add('tq-dragging', 'sheet-expanded');
      drag.drawer.style.setProperty('--tq-sheet-h', clampHeight(drag.startH - dy) + 'px');

      var dt = event.timeStamp - drag.lastT;
      if (dt > 0) drag.velocity = (event.clientY - drag.lastY) / dt; // px/ms, + = down
      drag.lastY = event.clientY;
      drag.lastT = event.timeStamp;

      event.preventDefault(); // stop the page/list scrolling under the drag
    },
    { passive: false }
  );

  function endDrag(event) {
    if (!drag) return;
    var d = drag;
    drag = null;
    d.drawer.classList.remove('tq-dragging');
    if (!d.moved) return; // it was a tap — leave it to the click handler

    suppressClickUntil = Date.now() + 400; // eat the trailing synthetic click
    var height = clampHeight(d.startH - (event.clientY - d.startY));
    var collapseAt = window.innerHeight * 0.3;

    if (d.velocity > 0.5 || (height < collapseAt && d.velocity >= -0.5)) {
      // flick down, or released low → collapse to the mini bar
      setSheetExpanded(d.drawer, false);
      d.drawer.style.removeProperty('--tq-sheet-h');
    } else if (d.velocity < -0.5) {
      // flick up → snap to full height
      setSheetExpanded(d.drawer, true);
      d.drawer.style.setProperty('--tq-sheet-h', clampHeight(window.innerHeight * 0.92) + 'px');
    } else {
      // released mid-way → keep exactly where the finger let go
      setSheetExpanded(d.drawer, true);
      d.drawer.style.setProperty('--tq-sheet-h', height + 'px');
    }
  }

  document.addEventListener('pointerup', endDrag);
  document.addEventListener('pointercancel', endDrag);

  // the sheet's close button and Escape collapse back to the mini bar on
  // mobile (the stock close() only removes .active, which the mini ignores)
  document.addEventListener('click', function (event) {
    var close = event.target.closest('.tq-drawer .drawer__close');
    if (!close) return;
    var drawer = close.closest('cart-drawer');
    if (drawer) setSheetExpanded(drawer, false);
  });
  document.addEventListener('keyup', function (event) {
    if (event.code !== 'Escape') return;
    var drawer = document.querySelector('cart-drawer.sheet-expanded');
    if (drawer) setSheetExpanded(drawer, false);
  });

  // ---- cascara slot (snippets/tq-cascara-slot.liquid) ----------------------
  // The slot's add button is a plain GET /cart/add link (works without JS and
  // on the cart page, where a reload re-renders everything). In the drawer it
  // becomes an AJAX add with the drawer + badge sections in the same request,
  // exactly like the catalog's add to cart (assets/tq-catalog.js addToCart),
  // and the new line gets .tq-arrive for its "dropped into the box" animation.
  var SLOT_OFF_KEY = 'tq_slot_off';

  document.documentElement.classList.add('tq-slot-js');
  try {
    if (sessionStorage.getItem(SLOT_OFF_KEY) === '1') document.documentElement.classList.add('tq-slot-off');
  } catch (e) {
    /* storage blocked — the slot simply stays available */
  }

  document.addEventListener('click', function (event) {
    var dismiss = event.target.closest('[data-tq-slot-dismiss]');
    if (dismiss) {
      document.documentElement.classList.add('tq-slot-off');
      try {
        sessionStorage.setItem(SLOT_OFF_KEY, '1');
      } catch (e) {
        /* storage blocked — hidden until the next page load */
      }
      var checkout = document.getElementById('CartDrawer-Checkout');
      if (checkout && dismiss.closest('cart-drawer')) checkout.focus();
      return;
    }

    var add = event.target.closest('[data-tq-slot-add]');
    if (!add) return;
    var drawer = add.closest('cart-drawer');
    if (!drawer || typeof drawer.renderContents !== 'function') return; // cart page: follow the link

    event.preventDefault();
    if (add.classList.contains('is-loading')) return;
    add.classList.add('is-loading');

    var variantId = add.getAttribute('data-variant-id');
    drawer.setActiveElement(add);
    fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1, properties: { _tq_src: 'slot' } }],
        sections: drawer.getSectionsToRender().map(function (section) {
          return section.id;
        }),
        sections_url: window.location.pathname,
      }),
    })
      .then(function (response) {
        if (!response.ok) throw new Error('cart/add ' + response.status);
        return response.json();
      })
      .then(function (state) {
        drawer.renderContents(state);
        var row = drawer.querySelector('[data-tq-variant="' + variantId + '"]');
        if (row) row.classList.add('tq-arrive');
        drawer.querySelectorAll('.mini-total, .tval').forEach(function (el) {
          el.classList.add('tq-bump');
        });
      })
      .catch(function (error) {
        console.error(error);
        // fall back to the plain link: Shopify adds the line and shows the cart page
        window.location.href = add.href;
      });
  });

  // Restore the visitor's saved region on a fresh cart. Only when a toggle is
  // rendered (Canadian visitors with a non-empty cart) and it disagrees with
  // the saved choice.
  document.addEventListener('DOMContentLoaded', function () {
    var bar = document.querySelector('[data-tq-freeship]');
    if (!bar || !bar.querySelector('[data-tq-region]')) return;
    var saved = readSaved();
    if ((saved === 'qc_on' || saved === 'rest') && saved !== bar.getAttribute('data-region')) {
      setRegion(saved, false);
    }
  });
})();
